export interface ReportSection {
  key: string
  label: string
  prompt: string
}

export const REPORT_SECTIONS: ReportSection[] = [
  {
    key: 'market_submarket_overview',
    label: 'Market & Submarket Overview',
    prompt:
      'Market & Submarket Overview\n' +
      'Subheader: A concise, bulleted summary of the property\'s economic context, demographics, and competitive position. Designed to be copy-pasted directly into the "Market" section of an Investment Committee memo.\n' +
      'Tags: Investor, Appraiser, Lender, Broker\n' +
      'Estimated Length: 1 Page (Bullet Points)\n' +
      'Prompt:\n' +
      'You are an institutional real estate investment analyst. Using the provided HelloData Excel export for property location and pipeline data, combined with your own knowledge of the local economy, create a **Market & Submarket Overview**.\n' +
      '\n' +
      '**Format Requirements:**\n' +
      '* **Style:** Strictly bullet points. Do not write long paragraphs.\n' +
      '* **Citations:** You must note the **Source** and **Date** for all demographic and economic figures (e.g., *Source: BLS, Dec 2024*).\n' +
      '\n' +
      '**Content Structure:**\n' +
      '\n' +
      '**1. Location Context**\n' +
      '* Describe the City/Neighborhood location in relation to the nearest Central Business District (CBD).\n' +
      '* Mention distance/drive time to major transit hubs or highways.\n' +
      '\n' +
      '**2. Economic Drivers & Demographics**\n' +
      '* **Population:** Current population estimate and net migration trends (if available). [Cite Source/Date]\n' +
      '* **Unemployment Rate:** Current local rate vs. national average. [Cite Source/Date]\n' +
      '* **Major Employers:** List the top 3-5 anchors in the immediate area. [Cite Source/Date]\n' +
      '* **Job Growth:** Recent year-over-year job growth rate. [Cite Source/Date]\n' +
      '* **Household Income:** Average and/or Median Household Income in the submarket. [Cite Source/Date]\n' +
      '* **Lifestyle & Convenience:** Bullet points on proximity to shopping (nearest grocer/retail center), entertainment, and lifestyle amenities.\n' +
      '\n' +
      '**3. Supply & Pipeline Analysis (Use HelloData Export)**\n' +
      '* *Instruction:* Use the "Rent Comps" and "Specials" tabs from the provided file for this section.\n' +
      '* **New Construction/Pipeline:** Discuss units coming online in the next 18 months (if visible in data) or recent deliveries.\n' +
      '* **Absorption:** Reference the "Leasing Trends" tab to describe current leasing velocity/absorption in the comp set.\n' +
      '* **Concessions:** Summarize the current concession environment (e.g., "avg 4 weeks free") based on the "Specials" tab.\n' +
      '\n' +
      '**Disclaimer:**\n' +
      "Please conclude with this text: Demographic and economic figures are based on external market data sources as cited. Pipeline and absorption data are derived from HelloData's aggregated listing data"
  },
  {
    key: 'competitive_set_analysis',
    label: 'Competitive Set Analysis (Comps)',
    prompt:
      'Competitive Set Analysis (Comps)\n' +
      'Subheader: A detailed comparison of the subject property against its direct competitors regarding rent, amenities, and finish levels. Used to benchmark performance and identify market positioning.\n' +
      'Tags: Broker, Investor, Asset Manager, Developer\n' +
      'Estimated Length: 1 Page (Text + Comparative Table)\n' +
      'Prompt: You are a multifamily market analyst. Using the provided HelloData Excel export (specifically the "Comps" and "Rent Comparison" tabs), create a "Competitive Set Analysis."\n' +
      '**Data Constraints:**\n' +
      '* You do not have actual occupancy. Use "Exposure" (units listed / total units) or "Advertised Leased %" as the metric.\n' +
      '* Rents are asking rents, not effective rents from a rent roll.\n' +
      '**Structure:**\n' +
      '1. **Comp Set Overview:** Briefly describe the quality and proximity of the selected comparable properties.\n' +
      '2. **Rent & Spec Benchmarking:** Compare the subject’s Asking Rent, Rent PSF, and Unit Sizes against the comp set averages.\n' +
      '3. **Amenity & Finish Comparison:** Analyze how the subject’s amenity package stacks up against the comps. Are we premium, discount, or at market?\n' +
      '4. **Performance Benchmarking:** Compare Days on Market (DOM) and Exposure levels.\n' +
      '5. **Summary Table:** Create a Markdown table comparing Subject vs. Comp Set Average for: Year Built, Units, Avg Rent, Avg SqFt, Rent PSF, and Advertised Leased %.\n' +
      '**Formatting:**\n' +
      '* Round rents to whole dollars.\n' +
      '* Show PSF with 2 decimals.\n' +
      '* Use clear headings.\n' +
      '**Disclaimer:**\n' +
      'Please conclude with this text: *This comparison utilizes advertised asking rents and availability data. Actual leased occupancy and effective rents may vary based on non-public lease terms.*'
  },
  {
    key: 'swot_analysis',
    label: 'SWOT Analysis',
    prompt:
      'SWOT Analysis\n' +
      'Subheader: A strategic summary of Strengths, Weaknesses, Opportunities, and Threats. Used for high-level decision-making in Investment Committee memos or Owner Reports.\n' +
      'Tags: Investor, Asset Manager, Owner, Lender\n' +
      'Estimated Length: 0.5 Pages (Bulleted List)\n' +
      'Prompt: You are a senior real estate strategist. Using the HelloData export, generate a high-impact *SWOT Analysis* for the subject property.\n' +
      '*Data Constraints:*\n' +
      'Focus on observable data: Pricing, Review Sentiment, Location, and Exposure. Do not infer private internal operations.\n' +
      '*Structure:*\n' +
      '* *Strengths:* Internal positive attributes (e.g., "Rents are 10% above market with high review sentiment," "Superior amenity package vs. comps").\n' +
      '* *Weaknesses:* Internal negative attributes (e.g., "High exposure on 2BR units," "Lower amenity density than new supply").\n' +
      '* *Opportunities:* External or strategic factors (e.g., "Renovation potential to close the $200 rent gap with newer comps," "Burn off concessions to match market tightening").\n' +
      '* *Threats:* External risks (e.g., "New supply delivering in Q4," "Aggressive concessions at neighboring property X").\n' +
      '*Formatting:*\n' +
      '* Use bold headers for each section.\n' +
      '* Provide 3-4 concise bullet points per section.\n' +
      '* Be specific—cite actual numbers (rent gaps, days on market) where possible.\n' +
      '*Disclaimer:*\n' +
      'Please conclude with this text: SWOT analysis is derived from public listing data, algorithm-estimated operations, and market benchmarks.'
  },
  {
    key: 'operational_action_plan',
    label: 'Operational Action Plan (Next 90 Days)',
    prompt:
      'Operational Action Plan (Next 90 Days)\n' +
      'Subheader: A prescriptive list of immediate actions to improve leasing velocity and revenue. Used by Property Managers and Asset Managers to guide onsite teams.\n' +
      'Tags: Property Manager, Asset Manager, Owner\n' +
      'Estimated Length: 1 Page\n' +
      'Prompt: You are a Multifamily Asset Manager. Using the HelloData export, create an *Operational Action Plan* for the next 90 days.\n' +
      '*Data Constraints:*\n' +
      '* *Critical:* We do not have real occupancy. Use "Advertised Leased %" and "Exposure."\n' +
      '* If properties hold back inventory, exposure numbers may differ from true vacancy. Address this by focusing on trends in exposure and pricing.\n' +
      '*Structure:*\n' +
      '1.  *Executive Snapshot:* Current Advertised Leased %, Exposure, and Leasing Velocity.\n' +
      '2.  *Pricing & Concession Strategy:* specific recommendations for 1BR, 2BR, etc. (e.g., "Reduce Asking Rent on 2BRs by 2% to combat 25+ days on market").\n' +
      '3.  *Marketing & Reputation:* Actionable steps based on review sentiment (e.g., "Address maintenance response complaints cited in 3 recent reviews").\n' +
      '4.  *Action Item Checklist:* A bulleted list of 5 prioritized tasks for the onsite team (e.g., "Launch 4-week look-and-lease special," "Refresh photos for Unit Type B").\n' +
      '*Formatting:*\n' +
      '* Be prescriptive and directive.\n' +
      '* Use bolding for key metrics.\n' +
      '*Disclaimer:*\n' +
      'Please conclude with this text: Recommendations are based on advertised data and market trends. Implementations should be validated against internal availability and budget constraints.'
  },
  {
    key: 'value_add_renovation_strategy',
    label: 'Value-Add & Renovation Strategy',
    prompt:
      'Value-Add & Renovation Strategy\n' +
      'Subheader: An analysis of the potential ROI for unit interior renovations. Used to determine if upgrading classic units to achieve higher premiums is financially viable.\n' +
      'Tags: Investor, Developer, Asset Manager\n' +
      'Estimated Length: 0.5 - 1 Page\n' +
      'Prompt: You are a Value-Add Real Estate Consultant. Using the HelloData export, generate a *Renovation & Premium Analysis*.\n' +
      '*Data Constraints:*\n' +
      '* Rent premiums are derived from listing data (Renovated vs. Classic listings).\n' +
      '* Renovation costs are industry benchmarks (not actual bids).\n' +
      '* You do not have the internal rent roll to know exactly how many classic units remain; frame this as an "opportunity based on market evidence."\n' +
      '*Structure:*\n' +
      '1.  *Rent Premium Analysis:* Compare average asking rents for "Renovated" vs. "Classic" units at the subject and comps. Quantify the potential lift (e.g., "+$150/month").\n' +
      '2.  *ROI Assessment:* Create a table showing: Unit Type | Est. Reno Cost (Use standard market assumptions) | Monthly Rent Premium | Est. ROI %.\n' +
      '3.  *Lease-Up Considerations:* Will higher rents slow down absorption? Check the Days on Market for premium units in the comps.\n' +
      '4.  *Recommendation:* Should the owner proceed with renovations? Which unit types should be prioritized?\n' +
      '*Formatting:*\n' +
      '* Use tables for the numbers.\n' +
      '* Round ROI to 1 decimal place.\n' +
      '*Disclaimer:*\n' +
      'Please conclude with this text: Premiums are estimated based on public listing disparities. Renovation costs are market benchmarks. Actual returns must be verified with contractor bids and internal rent rolls.'
  },
  {
    key: 'leasing_scripts_objection_handling',
    label: 'Leasing Scripts & Objection Handling',
    prompt:
      'Leasing Scripts & Objection Handling\n' +
      'Subheader: A guide for leasing agents containing sales scripts, talking points, and responses to common prospect objections based on competitor data.\n' +
      'Tags: Leasing Agent, Property Manager\n' +
      'Estimated Length: 1 Page\n' +
      'Prompt: You are a Top-Performing Leasing Trainer. Using the HelloData export (Unit Mix, Reviews, Amenities), create a *Leasing Playbook & Script Guide*.\n' +
      '*Data Constraints:*\n' +
      'Focus on "Advertised Attributes" and "Review Sentiment."\n' +
      '*Structure:*\n' +
      '1.  *"Why Us?" Pitch:* 3 bullet points highlighting unique value propositions (e.g., "Largest square footage in the submarket," "Higher rated maintenance than Competitor X").\n' +
      '2.  *Unit Positioning:* Specific one-liners for selling specific floor plans (e.g., "The 1BR layout offers a den that Competitor Y lacks").\n' +
      '3.  *Objection Handling:*\n' +
      '    * Objection: "You are more expensive than [Competitor Name]."\n' +
      '    * Script: "While our rent is slightly higher, we offer [X amenity/sqft] which saves you money on [Y]."\n' +
      '    * Objection: "I read reviews about noise."\n' +
      '    * Script: Provide a constructive response based on recent positive sentiment or management responses found in the data.\n' +
      '4.  *Closing Drivers:* Urgency tactics based on availability (e.g., "We only have 2 of these views left").\n' +
      '*Formatting:*\n' +
      '* Use conversational, persuasive language.\n' +
      '* Format as "Script:" blocks for easy copying.\n' +
      '*Disclaimer:*\n' +
      'Please conclude with this text: Scripts are generated based on comparative listing data and sentiment analysis. Verify amenity availability before touring.'
  },
  {
    key: 'risks_mitigants_analysis',
    label: 'Risks & Mitigants Analysis',
    prompt:
      'Risks & Mitigants Analysis\n' +
      'Subheader: An assessment of potential risks to the asset’s performance (market, operational, financial) and proposed strategies to mitigate them.\n' +
      'Tags: Lender, Investor, Asset Manager\n' +
      'Estimated Length: 0.5 - 1 Page\n' +
      'Prompt: You are a Risk Manager for a real estate fund. Using the HelloData export, draft a *Risk & Mitigation Analysis*.\n' +
      '*Data Constraints:*\n' +
      '* Do not reference private debt service coverage ratios (DSCR) or loan covenants as you do not have the loan documents.\n' +
      '* Focus on Market Risks, Supply Risks, and Leasing Risks.\n' +
      '*Structure:*\n' +
      'Identify 3-5 Key Risks based on the data. For each, follow this format:\n' +
      '* *Risk Factor:* (e.g., "Supply Saturation: 500 new units delivering in submarket within 12 months.")\n' +
      '* *Probability/Impact:* (e.g., "High Probability / Moderate Impact on rent growth.")\n' +
      '* *Mitigation Strategy:* (e.g., "Focus on retention of existing residents and offer 12-month renewal incentives to bridge the supply wave.")\n' +
      '*Key areas to check in data:*\n' +
      '* Is "Exposure" trending up?\n' +
      '* Are competitor concessions increasing?\n' +
      '* Is the "Advertised Leased %" dropping?\n' +
      '*Disclaimer:*\n' +
      'Please conclude with this text: Risk assessment is based on public market indicators and algorithmic forecasts. It does not include physical due diligence or title/legal review.'
  },
  {
    key: 'estimated_financial_performance',
    label: 'Estimated Financial Performance (Pro Forma)',
    prompt:
      'Estimated Financial Performance (Pro Forma)\n' +
      'Subheader: A 10-year projected financial model summary based on market benchmarks and stabilized operating assumptions.\n' +
      'Tags: Investor, Appraiser, Broker\n' +
      'Estimated Length: 1 Page (Text + Key Metrics)\n' +
      'Prompt: You are an Investment Analyst. Using the "Financial Analysis" tab in the HelloData export, write a narrative summary of the *Estimated Financial Performance*.\n' +
      '*Data Constraints:*\n' +
      '* *Crucial:* This is NOT historical performance (T-12). This is a "Stabilized Benchmark" model based on how the asset should perform if managed like a typical peer in this market.\n' +
      '* Do not present this as actual historicals.\n' +
      '*Structure:*\n' +
      '1.  *Revenue Assumptions:* Discuss the Gross Potential Rent (derived from current listing prices) and Market Vacancy assumptions.\n' +
      "2.  *Expense Benchmarks:* Summarize the estimated operating expenses. Explain that these are derived from regional operating benchmarks, not the owner's checkbook.\n" +
      '3.  *NOI & Valuation:* Present the estimated Net Operating Income (NOI). If a Cap Rate is provided in the analysis, calculate the implied valuation.\n' +
      '4.  *10-Year Outlook:* Briefly summarize the projected growth trajectory (e.g., "Assuming 3% annual rent growth...").\n' +
      '*Formatting:*\n' +
      '* Use professional financial terminology.\n' +
      '* Clearly distinguish between "Gross Potential" and "Effective" revenue.\n' +
      '*Disclaimer:*\n' +
      'Please conclude with this text: This financial analysis represents a theoretical stabilized pro forma based on market benchmarks. It does not reflect actual historical operating statements (T-12) or owner-specific expenses.'
  },
  {
    key: 'ancillary_revenue_audit',
    label: 'Ancillary Revenue Audit (Fees & Other Income)',
    prompt:
      'Ancillary Revenue Audit (Fees & Other Income)\n' +
      'Subheader: A comparative audit of non-rent revenue streams (parking, pet fees, admin fees) to identify where the subject property is leaving money on the table compared to competitors.\n' +
      'Tags: Asset Manager, Property Manager, Owner\n' +
      'Estimated Length: 0.5 Pages (Text + Fee Comparison Table)\n' +
      'Prompt: You are an Asset Manager focused on maximizing "Other Income." Using the *Fees & Amenities* tab in the HelloData export, generate an *Ancillary Revenue Audit*.\n' +
      '*Data Constraints:*\n' +
      '* Compare advertised fees only.\n' +
      '* If a fee is blank in the data, note it as "Not Advertised" rather than $0.\n' +
      '*Structure:*\n' +
      '1.  *Fee Benchmarking Table:* Create a Markdown table comparing the Subject vs. Comp Set Average for:\n' +
      '    * Application Fee\n' +
      '    * Admin Fee\n' +
      '    * Pet Rent (Dog/Cat) & Non-Refundable Pet Fees\n' +
      '    * Parking (Reserved/Unreserved/Garage)\n' +
      '2.  *Revenue Opportunity Analysis:* Identify specific line items where the subject is charging below market. (e.g., "Subject charges $150 for parking while the comp set average is $225, representing a potential $900/yr per car opportunity.")\n' +
      '3.  *Policy Recommendations:* Recommend adjustments to fee structures based on the data (e.g., "Increase Admin Fee to $500 to match market standard").\n' +
      '*Formatting:*\n' +
      '* Use clear bold headers.\n' +
      '* Quantify the potential annualized revenue impact of raising fees to market levels (e.g., "$75 increase x 100 spots = $90k/year").\n' +
      '*Disclaimer:*\n' +
      'Please conclude with this text: Fee analysis is based on advertised charges found in public listings. Actual lease-specific ancillary charges may vary.'
  },
  {
    key: 'floorplan_efficiency_unit_mix_analysis',
    label: 'Floorplan Efficiency & Unit Mix Analysis',
    prompt:
      'Floorplan Efficiency & Unit Mix Analysis\n' +
      'Subheader: A granular analysis of which specific floorplans generate the highest Rent PSF. Used by developers and owners to determine which unit types are the most efficient revenue generators.\n' +
      'Tags: Developer, Architect, Investor, Asset Manager\n' +
      'Estimated Length: 1 Page\n' +
      'Prompt: You are a Multifamily Architect and Strategist. Using the *Unit Mix* and *Rents by Unit Type* tabs, create a *Floorplan Efficiency Analysis*.\n' +
      '*Data Constraints:*\n' +
      '* Focus on Rent Per Square Foot (PSF) as the primary efficiency metric.\n' +
      '* Use "Exposure" to judge demand (high exposure = low demand).\n' +
      '*Structure:*\n' +
      '1.  *Efficiency Leaders:* Identify the top 3 floorplans by Rent PSF. Describe their characteristics (e.g., "Small studios under 500 sqft are achieving $4.50 PSF, the highest in the building").\n' +
      '2.  *Inefficiency Flags:* Identify floorplans with low Rent PSF and high exposure. (e.g., "The \'Large 2BR\' layout (1,200 sqft) trades at a 15% discount PSF and has 10% exposure, suggesting the market does not value the extra space").\n' +
      '3.  *Comp Comparison:* How does the subject’s average unit size compare to the comps? Are units too large (diluting PSF) or too small (limiting functionality)?\n' +
      '4.  *Future Design/Reno Recommendations:* For future renovations or new phases, which unit types should be maximized or minimized?\n' +
      '*Formatting:*\n' +
      '* Use bullet points for specific floorplan mentions.\n' +
      '* Highlight "Winner" and "Loser" layouts.\n' +
      '*Disclaimer:*\n' +
      'Please conclude with this text: Analysis is based on advertised rents and unit sizes. Actual floorplan popularity depends on physical attributes not always visible in data.'
  },
  {
    key: 'concession_competitiveness_strategy',
    label: 'Concession Competitiveness & Strategy',
    prompt:
      'Concession Competitiveness & Strategy\n' +
      'Subheader: A deep dive into the "Specials" history to understand how competitors use incentives to drive traffic. Used to formulate a burn-off strategy or tactical special.\n' +
      'Tags: Revenue Manager, Leasing Manager, Marketing Director\n' +
      'Estimated Length: 0.5 - 1 Page\n' +
      'Prompt: You are a Pricing Strategist. Using the *Specials* tab (which contains current and historical concession text) and the *Data* tab, create a *Concession Competitiveness Report*.\n' +
      '*Data Constraints:*\n' +
      '* Analyze the text of the specials to distinguish between "Upfront" (e.g., 1 Month Free) vs. "Look & Lease" (e.g., credited admin fees).\n' +
      '* Note the "From Date" and "To Date" to identify duration.\n' +
      '*Structure:*\n' +
      '1.  *Current Incentive Landscape:* Summary of what the competitive set is currently offering (e.g., "3 out of 5 comps are offering at least 4 weeks free").\n' +
      '2.  *Subject Position:* Is the subject property’s offer competitive, aggressive, or passive compared to the set?\n' +
      '3.  *Historical Trends:* Look at the "Historical Specials" data. Do comps typically ramp up specials in Q4/Q1? Identify seasonal patterns.\n' +
      '4.  *Strategic Recommendation:* Should the subject property offer "Weeks Free" (high face rent preservation) or "Lower Base Rent" (occupancy driver)? Base this on the current "Exposure" levels found in the Data tab.\n' +
      '*Formatting:*\n' +
      '* Quote specific text from competitor specials if relevant.\n' +
      '*Disclaimer:*\n' +
      "Please conclude with this text: Concession analysis is based on advertised specials text and validity dates. Unadvertised 'pocket specials' may exist. "
  },
  {
    key: 'seasonality_leasing_velocity_guide',
    label: 'Seasonality & Leasing Velocity Guide',
    prompt:
      'Seasonality & Leasing Velocity Guide\n' +
      'Subheader: An analysis of the Leasing Trends tab to visualize peak and trough leasing months. Used to predict cash flow dips and plan lease expiration management.\n' +
      'Tags: Asset Manager, Owner, Lender\n' +
      'Estimated Length: 1 Page (Text + Seasonality Table)\n' +
      'Prompt: You are a Portfolio Manager. Using the *Leasing Trends* tab (which shows leases signed by month), create a *Seasonality & Leasing Velocity Guide*.\n' +
      '*Data Constraints:*\n' +
      '* This data represents velocity (number of units leased per month), not occupancy.\n' +
      '* Use this to identify the property\'s specific "leasing season."\n' +
      '*Structure:*\n' +
      '1.  *Peak Season Identification:* Based on the last 12-24 months of data, identify which months had the highest lease volume for the subject and comps.\n' +
      '2.  *Winter Slowdown Analysis:* Quantify the drop-off in velocity during Nov/Dec/Jan. (e.g., "Leasing volume drops 40% in Q4 compared to Q2").\n' +
      '3.  *Lease Expiration Strategy:* Based on these trends, recommend when lease expirations should be clustered. (e.g., "Avoid ending leases in January; push expirations to May-August where velocity is 2x higher").\n' +
      '4.  *Velocity Benchmarking:* Is the subject property capturing its "fair share" of leases in the submarket compared to the comps?\n' +
      '*Formatting:*\n' +
      '* Create a simple text table showing "Avg Leases Signed" by Quarter.\n' +
      '* Use directive language for expiration management.\n' +
      '*Disclaimer:*\n' +
      'Please conclude with this text: Leasing velocity is derived from the detection of listings closing (going off-market). It serves as a proxy for turnover and demand.'
  }
]
